import React, { useContext, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { SNACKBAR_AUTO_HIDE_DURATION_SHORT } from '../../../constants/basic';
import {
  Avatar,
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  HelpOutlineOutlined,
  RotateRight,
  CheckCircleOutline,
  Description,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  white,
  gray100,
  gray40,
  gray80,
  gray60,
  red50,
  blue50,
  green50,
  tertiaryButtonBackgroundColor,
} from '../../../constants/colors';
import { AuthContext } from '../../../contexts/auth';
import { useAuthRedirect } from '../../../hooks/useAuthRedirect';
import { usePageLayout } from '../../../hooks/usePageLayout';
import useSnackbar from '../../../hooks/useSnackbar';
import SideBar from '../../uiParts/menu/SideBar';
import MetaTitle from '../../utils/MetaTitle';
import CustomLoading from '../../uiParts/form/CustomLoading';
import { boldXxl, regularSm, regularMd, spHeaderTitle } from '../../../constants/location';
import { watchFileExportHistories } from '../../../api/firebase/firestore/fileExportHistory/read';
import { deleteFileExportHistory } from '../../../api/firebase/firestore/fileExportHistory/delete';
import { updateFileExportHistoryFileName } from '../../../api/firebase/firestore/fileExportHistory/update';
import { FileExportHistory } from '../../../types/fileExportHistory';
import { fileExportProcessingStatus, FREE_OCR_PATTERN_ID } from '../../../constants/enum';
import { storage, auth } from '../../../api/firebase/config';
import { ref, getDownloadURL } from 'firebase/storage';
import dayjs from 'dayjs';
import DeleteModal from '../../uiParts/modal/DeleteModal';
import FileExportRenameDialog from '../../uiParts/modal/FileExportRenameDialog';
import CustomSnackbar from '../../uiParts/form/CustomSnackbar';
import { useCompanyId } from '../../../hooks/useCompanyId';
import HiStoryIcon from '../../assets/hi-story-icon.png';
import NoAccountIcon from '../../assets/Account circle.svg';
import { getDisplayName } from '../../../utils/displayName';

const styles = {
  header: {
    height: '80px',
    textAlign: 'center',
    backgroundColor: white,
    borderBottom: `1px solid ${gray40}`,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 24px',
    '@media (max-width: 960px)': {
      height: '48px',
      padding: '0 16px',
      borderTop: `1px solid ${gray40}`,
    },
  },
  headerTitle: {
    ...boldXxl,
    color: gray100,
    '@media (max-width: 960px)': {
      ...spHeaderTitle,
    },
  },
  mainContainer: {
    flexDirection: 'column',
    gap: '16px',
    width: 'calc(100% - 64px)',
    boxSizing: 'border-box',
    backgroundColor: white,
    margin: '0px 32px 16px 32px',
    padding: '24px',
    border: `1px solid ${gray40}`,
    borderRadius: '4px',
    '@media screen and (max-width:960px)': {
      margin: '0',
      width: '100%',
      border: 'none',
      borderRadius: 0,
    },
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  table: {
    minWidth: '960px',
  },
  tableHeadRow: {
    height: '48px',
  },
  tableHeadText: {
    ...regularSm,
    color: gray80,
    padding: '0 16px',
  },
  tableDataRow: {
    height: '48px',
    cursor: 'default',
    pointerEvents: 'none',
  },
  tableDataText: {
    ...regularSm,
    color: gray100,
    padding: '0 16px',
  },
  tableCellAvatar: {
    alignItems: 'center',
    display: 'flex',
    gap: '8px',
    height: '48px',
    padding: '0 16px',
  },
  avatar: {
    height: '24px',
    width: '24px',
  },
  buttonSmall: {
    height: '36px',
  },
  tableCellButton: {
    padding: 0,
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingRight: '8px',
  },
  showFileTitleText: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  statusContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '4px',
    color: gray60,
    '&[data-status="2"]': {
      color: green50,
    },
    '&[data-status="99"]': {
      color: red50,
      cursor: 'help',
    },
  },
  statusIcon: {
    fontSize: 22,
  },
  icon: {
    width: '56px',
    height: '56px',
    margin: '80px 0 24px 0',
    '@media screen and (max-width: 600px)': {
      margin: '24px 0 24px 0',
    },
  },
  title: {
    whiteSpace: 'pre-wrap',
    mb: 2,
    fontWeight: '500',
    color: gray100,
    fontSize: '22px',
    fontStyle: 'normal',
    lineHeight: '20px',
    '@media screen and (max-width: 600px)': {
      marginBottom: '12px',
    },
  },
  description: {
    whiteSpace: 'pre-wrap',
    mb: 4,
    ...regularMd,
    '@media screen and (max-width: 600px)': {
      marginBottom: '20px',
    },
  },
};

const FreeAiOcrExports: React.FC = () => {
  const { user, isLoading } = useContext(AuthContext);
  const { companyId: effectiveCompanyId } = useCompanyId();
  const { sideBarCollapsed, toggleSidebar, styles: layoutStyles } = usePageLayout();
  const navigate = useNavigate();
  // 未認証ならログイン画面へ退避する（元URLは ?redirect= で引き継ぐ。#3203）
  useAuthRedirect();

  const [histories, setHistories] = useState<FileExportHistory[]>([]);
  const [loading, setLoading] = useState(true);

  const [previousHistories, setPreviousHistories] = useState<FileExportHistory[]>([]);
  const [autoDownloadedIds, setAutoDownloadedIds] = useState<Set<string>>(new Set());
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string>('');

  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameTargetId, setRenameTargetId] = useState<string>('');
  const [renameTargetFileName, setRenameTargetFileName] = useState<string>('');

  const { errorText, infoText, snackbarOpen, setErrorText, setInfoText, setSnackbarOpen } =
    useSnackbar();

  useEffect(() => {
    if (!user || !effectiveCompanyId) return;

    setIsInitialLoadComplete(false);
    setPreviousHistories([]);
    setAutoDownloadedIds(new Set());
    setLoading(true);

    const unsubscribe = watchFileExportHistories(
      FREE_OCR_PATTERN_ID,
      effectiveCompanyId,
      (newHistories): void => {
        setHistories(newHistories);
        setLoading(false);
      },
      (): void => {
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [user, effectiveCompanyId]);

  useEffect(() => {
    if (!isInitialLoadComplete) {
      if (histories.length > 0) {
        setIsInitialLoadComplete(true);
        setPreviousHistories(histories);
      }
      return;
    }

    histories.forEach(history => {
      const previousHistory = previousHistories.find(prev => prev.id === history.id);
      const existedBefore = !!previousHistory;
      const wasProcessing =
        previousHistory?.processing_status === fileExportProcessingStatus.processing;
      const isCompleted = history.processing_status === fileExportProcessingStatus.completed;
      const notDownloaded = history.id && !autoDownloadedIds.has(history.id);

      if (existedBefore && wasProcessing && isCompleted && notDownloaded) {
        void handleFileDownload(history);
        setAutoDownloadedIds(prev => new Set(prev).add(history.id as string));
      }
    });

    setPreviousHistories(histories);
  }, [histories, isInitialLoadComplete, previousHistories, autoDownloadedIds]);

  const handleDeleteClick = (id: string): void => {
    setDeleteTargetId(id);
    setDeleteModalOpen(true);
  };

  const handleDelete = async (): Promise<void> => {
    if (!deleteTargetId) return;
    try {
      await deleteFileExportHistory(deleteTargetId);
      setInfoText('ファイル出力履歴を削除しました');
      setSnackbarOpen(true);
      setDeleteModalOpen(false);
    } catch {
      setErrorText('削除に失敗しました');
      setSnackbarOpen(true);
    } finally {
      setDeleteTargetId('');
    }
  };

  const handleRenameClick = (id: string, fileName: string): void => {
    setRenameTargetId(id);
    setRenameTargetFileName(fileName);
    setRenameDialogOpen(true);
  };

  const handleRename = async (newFileName: string): Promise<void> => {
    if (!renameTargetId) return;
    try {
      await updateFileExportHistoryFileName(renameTargetId, newFileName);
      setInfoText('ファイル名を変更しました');
      setSnackbarOpen(true);
      setRenameDialogOpen(false);
    } catch (error) {
      Sentry.captureException(error);
      setErrorText('ファイル名の変更に失敗しました');
      setSnackbarOpen(true);
    } finally {
      setRenameTargetId('');
      setRenameTargetFileName('');
    }
  };

  const handleFileDownload = async (history: FileExportHistory): Promise<void> => {
    if (history.processing_status !== fileExportProcessingStatus.completed) return;
    try {
      await auth.currentUser?.getIdToken(true);
      const fileRef = ref(storage, history.file_path);
      const url = await getDownloadURL(fileRef);
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = history.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      setErrorText('ファイルのダウンロードに失敗しました');
      setSnackbarOpen(true);
    }
  };

  if (isLoading) {
    return (
      <Box sx={layoutStyles.pageWrapper}>
        <MetaTitle customTitle="フリーOCR ファイル出力履歴" />
        <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
          <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
        </Box>
        <Box sx={[layoutStyles.pageContent, sideBarCollapsed && layoutStyles.pageContentCollapsed]}>
          <CustomLoading loading={true} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={layoutStyles.pageWrapper}>
      <MetaTitle customTitle="フリーOCR ファイル出力履歴" />
      <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
        <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
      </Box>

      <Box
        sx={[
          layoutStyles.pageContent,
          sideBarCollapsed && layoutStyles.pageContentCollapsed,
          { gap: '16px' },
        ]}
      >
        <Box sx={layoutStyles.header}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <IconButton aria-label="戻る" onClick={(): void => navigate('/ai-ocr/free')}>
              <ArrowBack sx={{ fontSize: '24px', color: gray80 }} />
            </IconButton>
            <Typography sx={styles.headerTitle}>フリーOCR - ファイル出力履歴</Typography>
          </Box>
        </Box>

        <Box sx={styles.mainContainer}>
          {loading ? (
            <CustomLoading loading={true} />
          ) : histories.length === 0 ? (
            <>
              <Description sx={styles.icon} />
              <Typography sx={styles.title}>出力されたファイルがありません</Typography>
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={styles.description}>
                  結果画面からファイル出力するとファイル出力履歴に表示されます。
                  {'\n'}
                  結果画面の「出力」ボタンを実行してください。
                </Typography>
              </Box>
            </>
          ) : (
            <Box sx={styles.tableWrapper}>
              <Table sx={styles.table}>
                <TableHead>
                  <TableRow sx={styles.tableHeadRow}>
                    <TableCell sx={styles.tableHeadText}>出力日時</TableCell>
                    <TableCell sx={styles.tableHeadText}>ファイル名</TableCell>
                    <TableCell sx={styles.tableHeadText}>ステータス</TableCell>
                    <TableCell sx={styles.tableHeadText}>作成者</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {histories.map(history => (
                    <TableRow key={history.id} sx={styles.tableDataRow}>
                      <TableCell sx={styles.tableDataText}>
                        {dayjs(history.created_at.toDate()).format('YYYY/MM/DD HH:mm')}
                      </TableCell>
                      <TableCell sx={styles.tableDataText}>
                        {history.processing_status === fileExportProcessingStatus.processing ? (
                          <Typography>{history.file_name}</Typography>
                        ) : history.processing_status === fileExportProcessingStatus.completed ? (
                          <Typography
                            onClick={(): void => {
                              void handleFileDownload(history);
                            }}
                            sx={{
                              color: blue50,
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              pointerEvents: 'auto',
                              '&:hover': { opacity: 0.7 },
                            }}
                          >
                            {history.file_name}
                          </Typography>
                        ) : (
                          <Tooltip title={history.error_message || 'エラーが発生しました'}>
                            <Typography sx={{ color: red50 }}>{history.file_name}</Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell sx={styles.tableDataText}>
                        {history.processing_status === fileExportProcessingStatus.error ? (
                          <Tooltip title={history.error_message || 'エラーが発生しました'}>
                            <Box
                              sx={styles.statusContainer}
                              data-status={history.processing_status}
                            >
                              <HelpOutlineOutlined sx={styles.statusIcon} />
                              <span>出力エラー</span>
                            </Box>
                          </Tooltip>
                        ) : (
                          <Box sx={styles.statusContainer} data-status={history.processing_status}>
                            {((): JSX.Element | string => {
                              switch (history.processing_status) {
                                case fileExportProcessingStatus.processing:
                                  return <RotateRight sx={styles.statusIcon} />;
                                case fileExportProcessingStatus.completed:
                                  return <CheckCircleOutline sx={styles.statusIcon} />;
                                default:
                                  return '';
                              }
                            })()}
                            <span>
                              {((): string => {
                                switch (history.processing_status) {
                                  case fileExportProcessingStatus.processing:
                                    return '出力中';
                                  case fileExportProcessingStatus.completed:
                                    return '出力完了';
                                  default:
                                    return '';
                                }
                              })()}
                            </span>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell sx={styles.tableCellAvatar}>
                        <Avatar
                          src={
                            history.is_proxy
                              ? HiStoryIcon
                              : history.created_by_icon_url || NoAccountIcon
                          }
                          sx={styles.avatar}
                        />
                        <Typography>
                          <Tooltip
                            title={getDisplayName(history.created_by_name, history.is_proxy)}
                          >
                            <span style={styles.showFileTitleText}>
                              {getDisplayName(history.created_by_name, history.is_proxy)}
                            </span>
                          </Tooltip>
                        </Typography>
                      </TableCell>
                      <TableCell sx={styles.tableCellButton}>
                        <Box sx={styles.actionButtons}>
                          {history.processing_status === fileExportProcessingStatus.completed && (
                            <Button
                              onClick={(event): void => {
                                event.stopPropagation();
                                if (history.id) handleRenameClick(history.id, history.file_name);
                              }}
                              sx={[
                                tertiaryButtonBackgroundColor,
                                styles.buttonSmall,
                                { pointerEvents: 'auto' },
                              ]}
                            >
                              編集
                            </Button>
                          )}
                          <Button
                            onClick={(event): void => {
                              event.stopPropagation();
                              if (history.id) handleDeleteClick(history.id);
                            }}
                            sx={[
                              tertiaryButtonBackgroundColor,
                              styles.buttonSmall,
                              { pointerEvents: 'auto' },
                            ]}
                          >
                            削除
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>
      </Box>

      <DeleteModal
        open={deleteModalOpen}
        onClose={(): void => setDeleteModalOpen(false)}
        onDelete={handleDelete}
        title="ファイル出力履歴"
      />

      <FileExportRenameDialog
        open={renameDialogOpen}
        onClose={(): void => setRenameDialogOpen(false)}
        onSave={handleRename}
        currentFileName={renameTargetFileName}
      />

      <CustomSnackbar
        open={snackbarOpen}
        onClose={(): void => setSnackbarOpen(false)}
        text={errorText}
        infoText={infoText}
        autoHideDuration={SNACKBAR_AUTO_HIDE_DURATION_SHORT}
      />
    </Box>
  );
};

export default FreeAiOcrExports;
